/**
 * weaponArmed.ts — which weapons are flipped to "Modded" (armed) for the run.
 *
 * This is the RISK decision, kept SEPARATE from "has mods socketed":
 *  - A gun can have mods socketed (weaponModLoadout.getEquipped) but still be
 *    carried Standard (no risk) if it's NOT armed here.
 *  - Arming a gun puts its modded version (buffs + risk) into the run, and shows
 *    it on the "GOING IN" rack in the loadout screen.
 *
 * A gun should only be armable when it actually has ≥1 mod socketed — arming a
 * gun with no mods does nothing. The UI enforces that; this store is just the set.
 *
 * Persisted to its OWN localStorage key so it can't corrupt the main save.
 */
const KEY = 'goopzone_armed_weapons';

let _cache: Set<string> | null = null;
function load(): Set<string> {
  if (_cache) return _cache;
  try {
    const s = localStorage.getItem(KEY);
    const arr = s ? JSON.parse(s) : [];
    _cache = new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : []);
  } catch {
    _cache = new Set();
  }
  return _cache!;
}
function save() {
  try {
    localStorage.setItem(KEY, JSON.stringify(Array.from(load())));
  } catch {
    /* private mode */
  }
}

/** Is this weapon flipped to Modded (armed for the run)? */
export function isArmed(weaponId: string): boolean {
  return load().has(weaponId);
}

/** Set the armed flag explicitly. */
export function setArmed(weaponId: string, on: boolean): void {
  const s = load();
  if (on) s.add(weaponId);
  else s.delete(weaponId);
  save();
}

/** Flip the armed flag. Returns the new state. */
export function toggleArmed(weaponId: string): boolean {
  const next = !isArmed(weaponId);
  setArmed(weaponId, next);
  return next;
}

/** Every armed weapon id (Standard guns excluded). */
export function getArmedWeapons(): string[] {
  return Array.from(load());
}

/** Drop the armed flag for a gun (e.g. when its last mod is unsocketed). */
export function disarm(weaponId: string): void {
  setArmed(weaponId, false);
}
